import React, { useState } from 'react';
import {
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { StyledPanel } from 'src/components/StyledPanel';
import SectionTitle from './SectionTitle';
import { CommuteData } from './types';
import NumberField from 'src/components/NumberField';
import { StyledFlex } from 'src/components/StyledFlex';

interface Props {
  commute: CommuteData[];
  onChange: (commute: CommuteData[]) => void;
  onSave: () => Promise<void>;
}

const TRANSPORT_TYPES = [
  'car_diesel',
  'car_petrol',
  'car_lpg',
  'car_hybrid',
  'car_electric_vehicle',
  'car_unknown',
  'motorbike',
  'taxi',
  'bus',
  'tram',
  'metro',
  'train',
  'electric_scooter',
  'bike_or_walk',
  'none',
];

export default function CommutingSection(props: Props) {
  const { commute, onChange, onSave } = props;
  const { t } = useTranslation('legacyGhg', {
    keyPrefix: 'employee-survey',
  });

  const [changed, setChanged] = useState(false);

  const updateCommute = (
    index: number,
    field: keyof CommuteData,
    value: string | number | null
  ) => {
    const newData = [...commute];
    if (field === 'type_of_commute_transport') {
      newData[index][field] = value as string;
    } else {
      newData[index][field] = value as number | null;
    }
    onChange(newData);
    setChanged(true);
  };

  const addCommute = () => {
    const newData = [
      ...commute,
      {
        type_of_commute_transport: '',
        times_per_week: null,
        average_distance_one_way_km: null,
      },
    ];
    onChange(newData);
    setChanged(true);
  };

  const handleDeleteClick = (index: number) => {
    const newData = [...commute];
    newData.splice(index, 1);
    onChange(newData);
    setChanged(true);
  };

  return (
    <>
      <SectionTitle>{t('question1')}</SectionTitle>
      <StyledPanel>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('preview-typeof-transport')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('preview-times_per_week')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('preview-average_distance_one_way_km')}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight="bold">
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {commute.map((c, index) => (
              <TableRow key={index}>
                <TableCell>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={c.type_of_commute_transport}
                    onChange={(e) =>
                      updateCommute(
                        index,
                        'type_of_commute_transport',
                        e.target.value
                      )
                    }
                    placeholder={t('preview-typeof-transport')}
                  >
                    {TRANSPORT_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {t(type)}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell>
                  <NumberField
                    fullWidth
                    size="small"
                    value={c.times_per_week ?? ''}
                    onChange={(value) =>
                      updateCommute(index, 'times_per_week', value)
                    }
                    placeholder={t('preview-times_per_week')}
                    inputProps={{ min: 0, max: 7, step: 0.1 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={c.average_distance_one_way_km ?? ''}
                    onChange={(e) =>
                      updateCommute(
                        index,
                        'average_distance_one_way_km',
                        e.target.value ? Number(e.target.value) : null
                      )
                    }
                    placeholder={t('preview-average_distance_one_way_km')}
                    inputProps={{ min: 0, step: 0.1 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteClick(index)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <StyledFlex mt={2}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addCommute}
            size="small"
          >
            {t('add-another')}
          </Button>
          {changed && (
            <Button
              size="small"
              variant="contained"
              onClick={() => onSave().then(() => setChanged(false))}
            >
              {t('save')}
            </Button>
          )}
        </StyledFlex>
      </StyledPanel>
    </>
  );
}
